import { useState } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { JurisdictionCombobox } from '../../tax-settings/JurisdictionCombobox';
import { useJurisdictionCatalog } from '../../tax-settings/useJurisdictionCatalog';
import { EoInput, EoSelect } from '../components/Fields';
import { SectionFrame } from '../components/SectionFrame';
import { employeeOnboardingGateway } from '../index';
import { StateElectionsSelfSection } from './StateElectionsSelfSection';
import type { TaxSectionProps } from './taxProps';
import { buildStateInput, moneyError, moneyText, parseMoney } from './taxValidation';

interface StateForm {
  stateCode: string;
  filingStatus: string;
  allowances: string;
  mostRecent: string;
}
type StateErrors = Partial<Record<keyof StateForm, string>>;

/** State Income Tax (Figma 04). The state is one of the jurisdictions DERIVED from the work and residence addresses, chosen in the shared jurisdiction combobox
 * (never free input). A territory has no state election in EPN: it says so, and nothing is saved for it. */
export function StateSection({ taxes, refresh, next, back }: TaxSectionProps) {
  const { options, draft, ruleNote } = taxes.state;
  const catalog = useJurisdictionCatalog();
  const [form, setForm] = useState<StateForm>({
    stateCode: draft?.stateCode ?? '',
    filingStatus: draft?.filingStatus ?? '',
    allowances: draft?.allowances === null || draft?.allowances === undefined ? '' : String(draft.allowances),
    mostRecent: moneyText(draft?.mostRecentWithholdingAmount),
  });
  const [errors, setErrors] = useState<StateErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const option = options.find((o) => o.code === form.stateCode);
  const noTax = option?.ruleKind === 'NoWageIncomeTax';
  const territory = option?.ruleKind === 'NotYetSupported';
  const set = <K extends keyof StateForm>(key: K, value: StateForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  async function submit() {
    setError(null);
    if (options.length === 0) {
      // The state is not known yet, so there is nothing to answer: the employee can continue and payroll reviews it.
      next();
      return;
    }
    const e: StateErrors = {};
    if (!option) e.stateCode = 'State is required.';
    if (territory) {
      // EPN has no state election for a territory and the server refuses one, so nothing is saved: the employee continues.
      next();
      return;
    }
    if (option && !noTax) {
      if (!form.filingStatus) e.filingStatus = 'Filing status is required.';
      if (option.usesAllowances) {
        if (form.allowances.trim() === '') e.allowances = 'Total allowances is required.';
        else if (!/^\d{1,2}$/.test(form.allowances.trim())) e.allowances = 'Enter a whole number from 0 to 99.';
      }
    }
    const recent = moneyError(form.mostRecent, 'Most recent withholding amount', false);
    if (recent) e.mostRecent = recent;
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setBusy(true);
    try {
      const recentValue = parseMoney(form.mostRecent);
      // Saving this screen must not erase the additional-withholding request saved on Tax Settings: the server replaces both parts.
      await employeeOnboardingGateway.saveStateTax(
        buildStateInput(
          {
            stateCode: form.stateCode,
            filingStatus: noTax ? null : form.filingStatus,
            allowances: noTax || !option?.usesAllowances ? null : Number(form.allowances),
            mostRecentWithholdingAmount: noTax ? null : recentValue,
          },
          taxes.settings.additionalWithholding,
        ),
      );
      await refresh();
      next();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not save your state tax answers. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  function clearDraft() {
    setForm({ stateCode: '', filingStatus: '', allowances: '', mostRecent: '' });
    setErrors({});
    setError(null);
  }

  return (
    <>
      <SectionFrame title="State Income Tax" onSubmit={submit} onBack={back} onClearDraft={clearDraft} busy={busy} error={error}>
      {ruleNote && (
        <p className="eo-banner eo-banner--info" data-testid="state-rule-note">
          {ruleNote}
        </p>
      )}
      <div className="eo-grid">
        <JurisdictionCombobox
          label="State"
          required
          items={catalog.items}
          allowedCodes={options.map((o) => o.code)}
          value={form.stateCode}
          error={errors.stateCode}
          disabled={options.length === 0 || catalog.loading}
          loading={catalog.loading}
          onChange={(code) => {
            setForm({ stateCode: code, filingStatus: '', allowances: '', mostRecent: '' });
            setErrors({});
          }}
        />

        {territory ? (
          <p className="eo-note eo-grid__span2" data-testid="state-territory-note">
            {option?.name} is a U.S. territory. EPN has no state withholding form for it yet, so there is nothing to complete here and nothing is saved. Payroll tax
            calculation is not certified for it.
          </p>
        ) : noTax ? (
          <p className="eo-note eo-grid__span2" data-testid="state-no-tax-note">
            {option?.name} has no state wage income tax withholding form, so there is nothing to complete here.
          </p>
        ) : (
          <>
            <EoSelect
              label="Filing Status"
              required
              value={form.filingStatus}
              error={errors.filingStatus}
              disabled={!option}
              onChange={(e) => set('filingStatus', e.target.value)}
            >
              <option value="">Select</option>
              {(option?.filingStatuses ?? []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </EoSelect>
            {(option?.usesAllowances ?? true) && (
              <EoInput
                label="Total Allowances"
                required
                inputMode="numeric"
                autoComplete="off"
                placeholder="00.00"
                disabled={!option}
                value={form.allowances}
                error={errors.allowances}
                onChange={(e) => set('allowances', e.target.value.replace(/\D/g, '').slice(0, 2))}
              />
            )}
            <EoInput
              label="Most Recent Withholding Amount ($)"
              inputMode="decimal"
              autoComplete="off"
              placeholder="00.00"
              disabled={!option}
              value={form.mostRecent}
              error={errors.mostRecent}
              hint="For reference only. It is not used to calculate withholding."
              onChange={(e) => set('mostRecent', e.target.value)}
            />
          </>
        )}
      </div>
      {catalog.failed && (
        <p className="eo-banner eo-banner--error" role="alert" data-testid="state-catalog-error">
          The list of states could not be loaded.{' '}
          <button type="button" className="eo-link-button" onClick={catalog.retry}>
            Try again
          </button>
        </p>
      )}
      {options.length === 0 && (
        <p className="eo-note" data-testid="state-unavailable-note">
          Your state is set from your Home/Residence Address and the work location your employer assigned. It is not available yet, so you can continue.
          Your payroll administrator will review it.
        </p>
      )}
      </SectionFrame>
      {/* EPN-52 PR 5: the real, reviewed election. It sits outside the draft form above: it is its own form and must never nest in another. */}
      <StateElectionsSelfSection />
    </>
  );
}
